/**
 * The build's configuration, read once and validated here.
 *
 * Reading `import.meta.env` from anywhere else is what let a missing API base
 * spread quietly through the old app: every caller had to remember the value
 * might be absent, and none did. This module fails loudly at startup instead,
 * where the message is about configuration rather than a fetch to `undefined/...`.
 */

const required = (name: keyof ImportMetaEnv): string => {
  const value = import.meta.env[name];

  if (!value) {
    throw new Error(
      `Missing ${name}. Add it to .env.${import.meta.env.MODE} and restart the dev server ` +
        '(Vite only reads env files at startup).',
    );
  }

  return value;
};

/** API base with any trailing slashes removed, so callers can always add one. */
export const API_URL = required('VITE_API_URL').replace(/\/+$/, '');

/** Kibana deep link. Optional: not every deployment has one. */
export const ELK_URL = import.meta.env.VITE_ELK_URL ?? null;

export const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Flight schedule';

/**
 * Which build this is, in three parts.
 *
 * All stamped in at build time; see `define` in vite.config.ts. Each falls back to
 * `''` for a toolchain that did not substitute it, so nothing can ever render the
 * literal `__APP_VERSION__` at somebody — and every reader has to say what it does
 * with an empty one.
 *
 * The footer shows the VERSION and puts the other two on its tooltip. Worth knowing
 * which way round that is: the version names the release — one bump per deploy, by the
 * rules in CHANGELOG.md — while the commit names this one bundle, so it is the commit
 * that settles whether the deploy somebody just ran is the one serving, and the commit
 * a reader can quote into a `git show`. On screen the readable one; the exact one is a
 * hover away.
 */
export const APP_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';

/** The short SHA, `-dirty` when built over uncommitted edits. */
export const APP_COMMIT: string = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : '';

/** ISO 8601, or '' — format it with the reader's locale rather than printing it raw. */
export const APP_BUILT_AT: string =
  typeof __APP_BUILT_AT__ === 'string' ? __APP_BUILT_AT__ : '';

export const IS_PRODUCTION = import.meta.env.PROD;
